import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { HeyGenImportStatusDto } from "shared";
import { io, type Socket } from "socket.io-client";
import { Api } from "../api.config";
import { QueryKeys } from "../LinkGen";

/**
 * Live per-item progress for a HeyGen import: socket pushes with a 2s poll
 * as a safety net. Bump `generation` (e.g. after a retry) to resume polling
 * on a batch that had already finished.
 */
export function useHeyGenImportStream(
	companyId: number,
	importId: number | null,
	generation = 0,
	onStatus?: (status: HeyGenImportStatusDto) => void,
): void {
	const queryClient = useQueryClient();
	const onStatusRef = useRef(onStatus);
	onStatusRef.current = onStatus;

	// biome-ignore lint/correctness/useExhaustiveDependencies: generation restarts the stream after a retry
	useEffect(() => {
		if (importId === null) {
			return;
		}

		const queryKey = QueryKeys.Integrations.HeyGen.Import(companyId, importId);
		let closed = false;
		let pollTimer: ReturnType<typeof setInterval> | null = null;
		let socket: Socket | null = null;

		const isFinished = (status: HeyGenImportStatusDto) =>
			status.status !== "in_progress";

		const applyStatus = (status: HeyGenImportStatusDto) => {
			queryClient.setQueryData(queryKey, status);
			onStatusRef.current?.(status);
		};

		const stopPolling = () => {
			if (pollTimer) {
				clearInterval(pollTimer);
				pollTimer = null;
			}
		};

		const pollOnce = async () => {
			try {
				const res = await Api.HeyGenImportStatus(importId);
				if (closed) {
					return;
				}
				applyStatus(res.data);
				if (isFinished(res.data)) {
					stopPolling();
				}
			} catch {
				// Keep polling until the import finishes or the modal closes.
			}
		};

		const startPolling = () => {
			if (pollTimer || closed) {
				return;
			}
			void pollOnce();
			pollTimer = setInterval(() => {
				void pollOnce();
			}, 2000);
		};

		startPolling();

		socket = io("/heygen-import", {
			withCredentials: true,
			transports: ["websocket", "polling"],
		});

		socket.on("connect", () => {
			socket?.emit("subscribe", { importId });
		});

		socket.on("status", (status: HeyGenImportStatusDto) => {
			applyStatus(status);
			if (isFinished(status)) {
				stopPolling();
			}
		});

		socket.on("disconnect", startPolling);
		socket.on("connect_error", startPolling);
		socket.on("error", startPolling);

		return () => {
			closed = true;
			stopPolling();
			socket?.disconnect();
			socket = null;
		};
	}, [companyId, importId, generation, queryClient]);
}
